import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, isAfter } from 'date-fns';

export const CalendarPicker = ({ selectedDate, onDateSelect, onClose, maxDate = new Date() }) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const renderHeader = () => (
    <div className="calendar-header">
      <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="cal-nav-btn">
        <ChevronLeft size={18} />
      </button>
      <span className="cal-month-label">{format(currentMonth, 'MMMM yyyy')}</span>
      <button 
        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} 
        className="cal-nav-btn"
        disabled={isAfter(startOfMonth(addMonths(currentMonth, 1)), maxDate)}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );

  const renderDays = () => {
    const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return (
      <div className="calendar-days-header">
        {days.map(day => <div key={day} className="cal-day-name">{day}</div>)}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const isDisabled = isAfter(day, maxDate);
        const isSelected = isSameDay(day, selectedDate);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isToday = isSameDay(day, new Date());

        days.push(
          <div
            key={day.toString()}
            className={`cal-day ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && onDateSelect(cloneDay)}
          >
            {format(day, 'd')}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<div key={day.toString()} className="cal-week">{days}</div>);
      days = [];
    }
    return <div className="calendar-body">{rows}</div>;
  };

  return (
    <div className="calendar-picker" ref={ref}>
      {renderHeader()}
      {renderDays()}
      {renderCells()}
      <div className="calendar-footer">
        <button className="cal-today-btn" onClick={() => { onDateSelect(new Date()); }}>Today</button>
      </div>
    </div>
  );
};

export const TimePicker = ({ selectedTime, onTimeSelect, onClose }) => {
  const [hours, setHours] = useState(selectedTime ? selectedTime.getHours() : 12);
  const [minutes, setMinutes] = useState(selectedTime ? selectedTime.getMinutes() : 0);
  const [seconds, setSeconds] = useState(selectedTime ? selectedTime.getSeconds() : 0);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleConfirm = () => {
    const newTime = new Date(selectedTime || new Date());
    newTime.setHours(hours, minutes, seconds, 0);
    onTimeSelect(newTime);
  };

  const ScrollableColumn = ({ value, onChange, max, label }) => (
    <div className="time-column">
      <div className="time-column-label">{label}</div>
      <button className="time-scroll-btn" onClick={() => onChange((value + 1) % (max + 1))}>
        <ChevronLeft size={16} style={{ transform: 'rotate(90deg)' }} />
      </button>
      <div className="time-value">{String(value).padStart(2, '0')}</div>
      <button className="time-scroll-btn" onClick={() => onChange(value === 0 ? max : value - 1)}>
        <ChevronRight size={16} style={{ transform: 'rotate(90deg)' }} />
      </button>
    </div>
  );

  return (
    <div className="time-picker" ref={ref}>
      <div className="time-picker-columns">
        <ScrollableColumn value={hours} onChange={setHours} max={23} label="HH" />
        <span className="time-separator">:</span>
        <ScrollableColumn value={minutes} onChange={setMinutes} max={59} label="MM" />
        <span className="time-separator">:</span>
        <ScrollableColumn value={seconds} onChange={setSeconds} max={59} label="SS" />
      </div>
      <div className="time-picker-footer">
        <button className="time-now-btn" onClick={() => {
          const now = new Date();
          setHours(now.getHours());
          setMinutes(now.getMinutes());
          setSeconds(now.getSeconds());
        }}>Now</button>
        <button className="time-confirm-btn" onClick={handleConfirm}>Go</button>
      </div>
    </div>
  );
};
